import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useTaxonomyContext } from '../context/TaxonomyContext';

import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';

const taxonomySchema = z.object({
  categoryName: z.string().min(1, 'Category is required'),
  type: z.string().min(1, 'Type is required'),
  groupName: z.string().min(1, 'Group Name is required'),
  property: z.string().min(1, 'Property is required'),
  description: z.string().min(1, 'Description is required'),
  examples: z.string().min(1, 'Examples are required'),
  filterValues: z.string().optional(),
  order: z.number().min(0, 'Order must be a positive number'),
});

type TaxonomyFormData = z.infer<typeof taxonomySchema>;

export function AddTaxonomyForm({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TaxonomyFormData>({
    resolver: zodResolver(taxonomySchema),
    defaultValues: {
      categoryName: '',
      type: '',
      groupName: '',
      property: '',
      description: '',
      examples: '',
      filterValues: '',
      order: 0,
    },
  });
  const { setTaxonomiesUpdated } = useTaxonomyContext();

  function onSubmit(data: TaxonomyFormData) {
    const updatedData = {
      ...data,
      filterValues: data.filterValues
        ?.split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    };

    setTaxonomiesUpdated((prev) => ({
      ...prev,
      data: [...prev.data, updatedData],
    }));

    toast.success('Taxonomy added, save changes at the end.');

    reset();
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Taxonomy</DialogTitle>

          <DialogDescription>
            Fill in the details for the new taxonomy property.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <FormGroup
                error={{ message: errors.categoryName?.message, font: 'sm' }}
              >
                <RequiredLabel htmlFor="category">Category</RequiredLabel>
                <Controller
                  name="categoryName"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </FormGroup>

              <FormGroup error={{ message: errors.type?.message, font: 'sm' }}>
                <RequiredLabel htmlFor="type">Type</RequiredLabel>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </FormGroup>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormGroup
                error={{ message: errors.groupName?.message, font: 'sm' }}
              >
                <RequiredLabel htmlFor="groupName">Group Name</RequiredLabel>
                <Controller
                  name="groupName"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </FormGroup>

              <FormGroup
                error={{ message: errors.property?.message, font: 'sm' }}
              >
                <RequiredLabel htmlFor="property">Property</RequiredLabel>
                <Controller
                  name="property"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
              </FormGroup>
            </div>

            <FormGroup
              error={{ message: errors.description?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="description">Description</RequiredLabel>
              <Controller
                name="description"
                control={control}
                render={({ field }) => <Textarea {...field} rows={3} />}
              />
            </FormGroup>

            <FormGroup
              error={{ message: errors.examples?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="examples">Examples</RequiredLabel>
              <Controller
                name="examples"
                control={control}
                render={({ field }) => <Textarea {...field} rows={3} />}
              />
            </FormGroup>

            <FormGroup
              error={{ message: errors.filterValues?.message, font: 'sm' }}
            >
              <Label htmlFor="filterValues">Filter Values</Label>
              <Controller
                name="filterValues"
                control={control}
                render={({ field }) => <Textarea {...field} rows={3} />}
              />
            </FormGroup>

            <FormGroup error={{ message: errors.order?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="order">Order</RequiredLabel>
              <Controller
                name="order"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                )}
              />
            </FormGroup>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Add Property</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
